import { makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';
import { Modal, Typography } from '@thingsw/pitta-design-system';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CLOUD_ONBOARDING_TOUR } from '../../screens/CameraPlaybackScreen';
import { useSelector } from 'react-redux';
import { RootState } from '../../features/store';
import { USER } from '../../features/User/slice';
import { Webviewer } from '@thingsw/pitta-modules';

const useStyles = makeStyles((theme: Theme) => ({
  headingText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: (props: any) => (props.mobile ? 'start' : 'center'),
    fontSize: (props: any) =>
      props.mobile ? '1.25rem !important' : '1.75rem !important',
  },
  root: {
    maxWidth: 1090,
    minHeight: 740,
  },
  contentDiv: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    padding: (props: any) => (props.mobile ? '0px' : '0 24px'),
  },
  itemDiv: {
    display: 'flex',
    flexDirection: (props: any) => (props.mobile ? 'column' : 'row'),
    gap: (props: any) => (props.mobile ? 16 : 44),
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  textDiv: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  img: {
    maxHeight: 186,
    objectFit: 'contain',
  },
  close: {
    top: (props: any) => (props.mobile ? 13 : 21),
  },
}));

interface CloudStorageTourModalProps {
  openTourModal: boolean;
  setOpenTourModal: (open: boolean) => void;
}

const CloudStorageTourModal = ({
  openTourModal,
  setOpenTourModal,
}: CloudStorageTourModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const classes = useStyles({ mobile });

  const { email } = useSelector((state: RootState) => state[USER]);

  const handleCloseTour = useCallback(() => {
    const tourString = localStorage.getItem(CLOUD_ONBOARDING_TOUR);
    const tour = JSON.parse(tourString ?? '[]');
    localStorage.setItem(
      CLOUD_ONBOARDING_TOUR,
      JSON.stringify([...tour, email])
    );

    setOpenTourModal(false);
  }, [email, setOpenTourModal]);

  return (
    <Modal
      open={openTourModal}
      close
      closeStyle={classes.close}
      onClose={handleCloseTour}
      heading={t('Cloud Storage Overview')}
      headingTextClassName={classes.headingText}
      className={classes.root}
      content={
        <div className={classes.contentDiv}>
          <div className={classes.itemDiv}>
            <div className={classes.textDiv}>
              <Typography variant='H5'>
                {t('What is Cloud Storage?')}
              </Typography>
              <Typography variant='Body'>{t('Cloud Storage is_')}</Typography>
            </div>

            <img
              className={classes.img}
              src='/images/cloud-storage-guide1.png'
              alt='cloud-storage-guide'
            />
          </div>

          <div className={classes.itemDiv}>
            <div className={classes.textDiv}>
              <Typography variant='H5'>
                {t('Difference Between FLEETA_')}
              </Typography>
              <ul
                style={{
                  paddingLeft: 24,
                  listStyle: 'disc',
                }}
              >
                <li>
                  <Typography variant='Body'>
                    {t('FLEETA Drive stores_')}
                  </Typography>
                </li>
                <li>
                  <Typography variant='Body'>
                    {t('Live Event Upload automatically_')}
                  </Typography>
                </li>
                <li
                  style={{
                    marginLeft: 16,
                  }}
                >
                  <Typography variant='Body'>
                    {t('Live Event Upload videos_')}
                  </Typography>
                </li>
              </ul>
            </div>

            <img
              className={classes.img}
              src='/images/cloud-storage-guide2.png'
              alt='cloud-storage-guide'
            />
          </div>

          <div className={classes.itemDiv}>
            <div className={classes.textDiv}>
              <Typography variant='H5'>{t('Note: SD Card_')}</Typography>
              <Typography variant='Body'>{t('Videos stored on_')}</Typography>
            </div>

            <img
              className={classes.img}
              src='/images/cloud-storage-guide3.png'
              alt='cloud-storage-guide'
            />
          </div>
        </div>
      }
    />
  );
};

export default CloudStorageTourModal;
